// src/components/TradeWorkflowPanel.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { db, storage, functions } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import useAccessProfile from "../hooks/useAccessProfile";
import {
  hasRole,
  isActiveUser,
  canApproveSellerPrep,
  canReviewInspection,
  canManagePickup,
  canManageDelivery,
  canManageTradeIssues,
} from "../utils/accessControl";
import "./TradeWorkflowPanel.css";

// --- helpers ---
const norm = (v) => String(v || "").trim();
const lower = (v) => norm(v).toLowerCase();

const toIsoOrNull = (localDT) => {
  const s = String(localDT || "").trim();
  if (!s) return null;
  const d = new Date(s);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toISOString();
};

const prettyKey = (k) =>
  String(k || "")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (m) => m.toUpperCase());

const uniqArr = (arr, keyFn) => {
  const seen = new Set();
  return (arr || []).filter((item) => {
    if (!item) return false;
    const key = keyFn ? keyFn(item) : item;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
const isSubmittedOrApproved = (s) => ["submitted", "approved"].includes(lower(s));

const HIGH_RISK_CATEGORY_KEYS = new Set([
  "electronics",
  "cellphones",
  "phones",
  "mobile_phones",
  "laptops",
  "computers",
  "gaming",
  "watches",
  "jewellery",
  "jewelry",
  "appliances",
]);

// Categories of items too large for partner drop-off locations
const LARGE_ITEMS_CATEGORY_KEYS = new Set([
  "vehicles",
  "cars",
  "motorcycles",
  "trucks",
  "home & garden",
  "furniture",
  "couches",
  "beds",
  "tables",
  "appliances",
  "refrigerators",
  "washers",
  "dryers",
  "dishwashers",
  "ovens",
  "sports & outdoors",
  "bicycles",
  "bikes",
  "garden equipment",
  "lawn mowers",
  "treadmills",
  "gym equipment",
]);

const supportsPartnerDropoff = (trade) => {
  if (!trade) return true; // default: allow if no trade data
  const categoryKey = lower(
    trade?.categoryKey ||
      trade?.category ||
      trade?.categoryLabel ||
      ""
  ).replace(/\s+/g, "_");
  return !LARGE_ITEMS_CATEGORY_KEYS.has(categoryKey);
};

const toMoneyNumber = (value) => {
  const n = Number(String(value ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const safeStr = (v) => {
  const s = norm(v);
  return s ? s : null;
};

const getFriendlyWorkflowError = (e, fallback) => {
  const message = String(e?.message || e?.details || "");
  if (/invalid workflow transition|failed-precondition/i.test(message)) {
    return (
      "This action is not available for the current workflow stage. " +
      "Please refresh and continue with the next available step."
    );
  }
  return fallback || message || "An error occurred. Please try again.";
};

const fmtDateTime = (v) => {
  if (!v) return "";
  const d =
    typeof v?.toDate === "function"
      ? v.toDate()
      : typeof v === "number"
      ? new Date(v)
      : new Date(String(v));
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleString();
};

async function toDownloadUrl(maybeUrlOrPath) {
  const v = String(maybeUrlOrPath || "").trim();
  if (!v) return "";

  if (v.startsWith("http") && v.includes("token=")) return v;

  try {
    if (v.startsWith("gs://") || v.startsWith("http")) {
      return await storage.refFromURL(v).getDownloadURL();
    }
  } catch {}

  try {
    return await storage.ref().child(v).getDownloadURL();
  } catch (e) {
    console.warn("Failed to resolve media url:", v, e);
    return "";
  }
}

async function uploadMany(files, pathPrefix, currentUser, onProgress, customMetadata) {
  const out = [];
  if (!files?.length) return out;

  for (let i = 0; i < files.length; i++) {
    const f = files[i];
    const safeName = `${Date.now()}_${Math.random().toString(16).slice(2)}_${f.name}`;
    const fullPath = `${pathPrefix}/${safeName}`;

    try {
      await currentUser?.getIdToken?.(true);
    } catch {}

    const ref = storage.ref().child(fullPath);

    const metadata = {
      contentType: f.type || (/\.(mp4)$/i.test(f.name) ? "video/mp4" : "application/octet-stream"),
      customMetadata: {
        ...(customMetadata || {}),
      },
    };

    const uploadTask = ref.put(f, metadata);

    await new Promise((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        (snap) => {
          const filePct = snap.totalBytes
            ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
            : 0;

          const overall = Math.round(((i + filePct / 100) / files.length) * 100);

          onProgress?.({
            overallPct: overall,
            filePct,
            index: i + 1,
            total: files.length,
            name: f.name,
            fullPath,
          });
        },
        (err) => reject(err),
        () => resolve()
      );
    });

    out.push({ url: await ref.getDownloadURL(), path: fullPath });
  }

  return out;
}

// -----------------------------
// Stage model (UI)
// -----------------------------
const STAGE_LABELS = {
  seller_prep: "Prepare Item",
  buyer_approval: "Buyer Confirmation",
  awaiting_payment: "Payment Needed",
  ready_for_inspection: "Choose Inspection Option",
  awaiting_dropoff: "Drop-off Needed",
  inspection_passed: "Inspection Approved",
  pickup_scheduled: "Pickup Booked",
  pickup_in_progress: "Pickup In Progress",
  delivery_scheduled: "Delivery Booked",
  delivered_pending_buyer_verification: "Confirm Handover",
  verification_passed: "Receipt Confirmed",
  completed: "Trade Completed",
  cancelled: "Trade Cancelled",
  dispute_open: "Issue Reported",
};

const STAGE_OWNERS = {
  seller_prep: "Seller",
  buyer_approval: "Buyer",
  awaiting_payment: "Buyer",
  ready_for_inspection: "Seller",
  awaiting_dropoff: "Seller",
  inspection_passed: "Admin",
  pickup_scheduled: "Inspector",
  pickup_in_progress: "Admin",
  delivery_scheduled: "Admin",
  delivered_pending_buyer_verification: "Buyer",
  verification_passed: "Admin",
  completed: "Completed",
  cancelled: "Closed",
  dispute_open: "Admin",
};

const getStageOwner = (stageKey, context = {}) => {
  const s = lower(stageKey);
  const { isPartnerDropoff, isPlatformPickup } = context;

  if (s === "ready_for_inspection") {
    return "Seller";
  }

  if (s === "awaiting_dropoff") {
    return "Seller";
  }

  if (s === "pickup_scheduled") {
    return isPlatformPickup ? "Inspector" : "Admin";
  }

  if (s === "inspection_passed") {
    return isPartnerDropoff ? "Admin" : "Admin";
  }

  return STAGE_OWNERS[s] || "Admin";
};

const ORDER = [
  "seller_prep",
  "buyer_approval",
  "awaiting_payment",
  "ready_for_inspection",
  "awaiting_dropoff",
  "inspection_passed",
  "pickup_scheduled",
  "pickup_in_progress",
  "delivery_scheduled",
  "delivered_pending_buyer_verification",
  "verification_passed",
  "completed",
];

const USER_PHASES = [
  {
    key: "accepted",
    label: "Accepted (Seller)",
    stages: ["seller_prep"],
  },
  {
    key: "confirm_pay",
    label: "Confirm & Pay (Buyer)",
    stages: ["buyer_approval", "awaiting_payment"],
  },
  {
    key: "inspection",
    label: "Inspection (Seller/Admin)",
    stages: [
      "ready_for_inspection",
      "awaiting_dropoff",
      "inspection_passed",
      "pickup_scheduled",
    ],
  },
  {
    key: "handover",
    label: "Handover (Admin/Buyer)",
    stages: [
      "pickup_in_progress",
      "delivery_scheduled",
      "delivered_pending_buyer_verification",
    ],
  },
  {
    key: "complete",
    label: "Complete (Buyer)",
    stages: ["verification_passed", "completed"],
  },
];

const getUserPhaseKey = (stage) => {
  const s = lower(stage);
  const hit = USER_PHASES.find((phase) => phase.stages.includes(s));

  if (s === "cancelled") return "complete";
  if (s === "dispute_open") return "handover";

  return hit?.key || "accepted";
};

const stageAlias = (rawStage) => {
  const s = lower(rawStage);

  if (Object.keys(STAGE_LABELS).includes(s)) return s;

  if (["locked_pending_risk_review", "risk_review"].includes(s)) return "seller_prep";
  if (["pickup_inspection_pass"].includes(s)) return "inspection_passed";
  if (["pickup_scheduled"].includes(s)) return "pickup_scheduled";
  if (["pickup_in_progress"].includes(s)) return "pickup_in_progress";
  if (["awaiting_dropoff"].includes(s)) return "awaiting_dropoff";
  if (["delivered_pending_buyer_verification"].includes(s)) return "delivered_pending_buyer_verification";
  if (["verification_passed"].includes(s)) return "verification_passed";
  if (["dispute_open", "held", "reopened"].includes(s)) return s;

  return s || "";
};

const getInspectionContext = ({ stage, isPartnerDropoff }) => {
  if (stage === "awaiting_dropoff") {
    return {
      title: "Partner Intake Inspection",
      shortLabel: "Partner Intake",
      inspectionType: "partner_dropoff_intake",
      inspectionMode: "partner_dropoff",
      hint:
        "Inspect the item at the approved drop-off point. Pass moves the trade forward. Fail cancels the workflow.",
    };
  }

  if (stage === "pickup_scheduled") {
    return {
      title: "Pickup Inspection",
      shortLabel: "Pickup Inspection",
      inspectionType: "pickup_inspection",
      inspectionMode: "platform_pickup",
      hint:
        "Inspect the item during the scheduled pickup. Pass moves the trade forward. Fail cancels the workflow.",
    };
  }

  if (stage === "ready_for_inspection") {
    if (isPartnerDropoff) {
      return {
        title: "Partner Intake Pending",
        shortLabel: "Awaiting Drop-off",
        inspectionType: "partner_dropoff_intake",
        inspectionMode: "partner_dropoff",
        hint:
          "The seller must first drop the item at the approved location. Inspection will happen at intake.",
      };
    }

    return {
      title: "Awaiting Pickup Scheduling",
      shortLabel: "Pre-Inspection",
      inspectionType: "pickup_inspection",
      inspectionMode: "platform_pickup",
      hint:
        "Pickup must be scheduled first. Inspection will happen during the pickup appointment.",
    };
  }

  return {
    title: "Inspection",
    shortLabel: "Inspection",
    inspectionType: isPartnerDropoff ? "partner_dropoff_intake" : "pickup_inspection",
    inspectionMode: isPartnerDropoff ? "partner_dropoff" : "platform_pickup",
    hint: "Submit the inspection outcome for this workflow.",
  };
};

function PanelSection({ title, active = false, children, innerRef }) {
  return (
    <div className={`twf-section ${active ? "twf-section--active" : ""}`} ref={innerRef}>
      <div className="twf-section-title">{title}</div>
      {children}
    </div>
  );
}

function StatusBlock({ title, hint, children }) {
  return (
    <div className="twf-section">
      <div className="twf-section-title">{title}</div>
      {hint ? <div className="twf-hint">{hint}</div> : null}
      {children ? <div className="twf-muted" style={{ marginTop: 10 }}>{children}</div> : null}
    </div>
  );
}

function getStageTone(stageKey, state) {
  if (state === "done") return "done";

  const s = String(stageKey || "").trim().toLowerCase();

 if (["seller_prep", "awaiting_payment"].includes(s)) {
    return "risk";
  }
  if (["buyer_approval"].includes(s)) {
    return "default";
  }

  if (
    [
      "ready_for_inspection",
      "awaiting_dropoff",
      "inspection_passed",
      "pickup_scheduled",
      "pickup_in_progress",
    ].includes(s)
  ) {
    return "inspection";
  }

  if (
    [
      "delivery_scheduled",
      "delivered_pending_buyer_verification",
      "verification_passed",
    ].includes(s)
  ) {
    return "delivery";
  }

  if (["completed"].includes(s)) {
    return "completed";
  }

  if (["cancelled", "dispute_open"].includes(s)) {
    return "risk";
  }

  return "default";
}

function PremiumStepRail({ timeline }) {
  return (
    <div className="twp-rail">
      <div className="twp-rail__items">
        {timeline.map((s) => {
          const tone = getStageTone(s.key, s.state);
          const isActive = s.state === "current";
          const isDone = s.state === "done";

          return (
            <div
              key={s.key}
              className={`twp-step-card twp-step-card--${tone} ${
                isActive ? "is-active" : ""
              } ${isDone ? "is-done" : ""}`}
            >
              <div className="twp-step-card__top">
                <div className="twp-step-card__dot-wrap">
                  <div className="twp-step-card__dot" />
                  {isActive ? <div className="twp-step-card__pulse" /> : null}
                </div>

                <div className={`twp-step-card__state twp-step-card__state--${tone}`}>
                  {isDone ? "Done" : isActive ? "You are here" : "Pending"}
                </div>
              </div>

              <div className="twp-step-card__label">{s.label}</div>

                {s.owner ? (
                  <div className="twp-step-card__owner">
                    For: {s.owner}
                  </div>
                ) : null}

                <div className="twp-step-card__key mono">{s.key}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function TradeWorkflowPanel({
  tradeId,
  onClose,
  mode: incomingMode = "full_workflow",
}) {
  const mode = incomingMode;

  const { currentUser } = useAuth();
  const { profile, loading: accessLoading, exists: accessExists } = useAccessProfile();
  const navigate = useNavigate();

  const uid = currentUser?.uid || "";

  const hasAccessProfile = !!currentUser ? accessExists : true;
  const accountIsActive = !!currentUser ? isActiveUser(profile) : false;

  const isAdminByRole = !!currentUser && hasRole(profile, "admin");
  const isAdminByEmail =
    !!currentUser?.email &&
    !!currentUser?.emailVerified &&
    String(currentUser.email).trim().toLowerCase().endsWith("@nexterzone.com");
  const isAdmin = isAdminByRole || isAdminByEmail;

  const canApproveSellerPrepPerm = !!currentUser && accountIsActive && canApproveSellerPrep(profile);
  const canReviewInspectionPerm = !!currentUser && accountIsActive && canReviewInspection(profile);
  const canManagePickupPerm = !!currentUser && accountIsActive && canManagePickup(profile);
  const canManageDeliveryPerm = !!currentUser && accountIsActive && canManageDelivery(profile);
  const canManageTradeIssuesPerm = !!currentUser && accountIsActive && canManageTradeIssues(profile);
  const canRunHubInspectionPerm =
    !!currentUser && accountIsActive && (canReviewInspectionPerm || canManagePickupPerm);

  const isSellerOfferMode = mode === "seller_offer";
  const isAdminMode = mode === "admin";
  const isFullWorkflowMode = mode === "full_workflow";

  const canSeeAdminSurface = isAdminMode && isAdmin;
  const canSeeWorkflowSummary = isAdminMode || isFullWorkflowMode;
  const canSeeSellerOrBuyerWorkflow = !isSellerOfferMode;

  const [wf, setWf] = useState(null);
  const [trade, setTrade] = useState(null);
  const [logistics, setLogistics] = useState(null);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [conditionGrade, setConditionGrade] = useState("good");
  const [notes, setNotes] = useState("");
  const [checklist, setChecklist] = useState({
    cleaned: false,
    tested: false,
    accessoriesIncluded: false,
    noPersonalInfo: false,
    packedSecurely: false,
  });
  const [photoFiles, setPhotoFiles] = useState([]);
  const [videoFiles, setVideoFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [photoPreviewUrls, setPhotoPreviewUrls] = useState([]);
  const [videoPreviewUrls, setVideoPreviewUrls] = useState([]);
  const [missingMediaShake, setMissingMediaShake] = useState({
    photos: false,
    videos: false,
  });

  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const [buyerApprovalBusy, setBuyerApprovalBusy] = useState(false);
  const [adminApprovalBusy, setAdminApprovalBusy] = useState(false);
  const [buyerJustApproved, setBuyerJustApproved] = useState(false);

  const [evidence, setEvidence] = useState({
    photos: [],
    videos: [],
    notes: "",
    conditionGrade: "",
  });

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadLabel, setUploadLabel] = useState("");
  const [uploadErr, setUploadErr] = useState("");
  const [justSubmitted, setJustSubmitted] = useState(false);

  const [pickupBusy, setPickupBusy] = useState(false);
  const [startPickupBusy, setStartPickupBusy] = useState(false);
  const [pickupWindowStart, setPickupWindowStart] = useState("");
  const [pickupWindowEnd, setPickupWindowEnd] = useState("");
  const [pickupAgentUid, setPickupAgentUid] = useState("");
  const [pickupPartnerRef, setPickupPartnerRef] = useState("");
  const [pickupNote, setPickupNote] = useState("");
  const [startPickupNote, setStartPickupNote] = useState("");

  const [pickupInspectionBusy, setPickupInspectionBusy] = useState(false);
  const [hubInspectionBusy, setHubInspectionBusy] = useState(false);
  const [deliveryBusy, setDeliveryBusy] = useState(false);
  const [markDeliveredBusy, setMarkDeliveredBusy] = useState(false);
  const [completeBusy, setCompleteBusy] = useState(false);

  const [hubInspectionNote, setHubInspectionNote] = useState("");
  const [deliveryWindowStart, setDeliveryWindowStart] = useState("");
  const [deliveryWindowEnd, setDeliveryWindowEnd] = useState("");
  const [deliveryPartnerRef, setDeliveryPartnerRef] = useState("");
  const [deliveryNote, setDeliveryNote] = useState("");
  const [deliveryPhotoFiles, setDeliveryPhotoFiles] = useState([]);
  const [deliveryPhotos, setDeliveryPhotos] = useState([]);
  const [deliveryUploadBusy, setDeliveryUploadBusy] = useState(false);
  const [deliveryUploadError, setDeliveryUploadError] = useState("");

  const [pickupInspectionNotes, setPickupInspectionNotes] = useState("");
  const [pickupInspectionSerialMatch, setPickupInspectionSerialMatch] = useState(true);
  const [pickupInspectionConditionMatch, setPickupInspectionConditionMatch] = useState(true);
  const [pickupInspectionPhotoFiles, setPickupInspectionPhotoFiles] = useState([]);
  const [pickupInspectionUploadOpen, setPickupInspectionUploadOpen] = useState(false);
  const [pickupInspectionUploadPct, setPickupInspectionUploadPct] = useState(0);
  const [pickupInspectionUploadLabel, setPickupInspectionUploadLabel] = useState("");

  const [buyerIssueBusy, setBuyerIssueBusy] = useState(false);
  const [buyerIssueReason, setBuyerIssueReason] = useState("");
  const [buyerIssueNote, setBuyerIssueNote] = useState("");

  const [inspectionFeePayBusy, setInspectionFeePayBusy] = useState(false);
  const [inspectionMethodBusy, setInspectionMethodBusy] = useState(false);
  const [selectedDropoffPartnerId, setSelectedDropoffPartnerId] = useState("");
  const [dropoffPartners, setDropoffPartners] = useState([]);
  const [dropoffPartnersLoading, setDropoffPartnersLoading] = useState(false);
  const [buyerConfirmBusy, setBuyerConfirmBusy] = useState(false);

  const actionsTopRef = useRef(null);
  const sellerPrepRef = useRef(null);
  const adminReviewRef = useRef(null);
  const buyerApprovalRef = useRef(null);
  const inspectionMethodRef = useRef(null);
  const inspectionFeeRef = useRef(null);
  const adminPickupInspectionRef = useRef(null);
  const adminSchedulePickupRef = useRef(null);
  const adminStartPickupRef = useRef(null);
  const adminScheduleDeliveryRef = useRef(null);
  const buyerReviewRef = useRef(null);
  const adminCompleteRef = useRef(null);

  const didAutoScrollRef = useRef(false);
  const uploadCloseTimerRef = useRef(null);
  const lastTradeDocIdRef = useRef("");
  const lastLogisticsDocIdRef = useRef("");
  

  useEffect(() => {
    if (!tradeId) return;

    setLoading(true);
    setErr("");

    let tradeUnsub = null;
    let logisticsUnsub = null;

    const wfUnsub = db
      .collection("TradeWorkflows")
      .doc(tradeId)
      .onSnapshot(
        (snap) => {
          const nextWf = snap.exists ? { id: snap.id, ...snap.data() } : null;
          setWf(nextWf);
          setLoading(false);

          const actualTradeId = nextWf?.tradeId || nextWf?.tradeRefId || "";

          if (actualTradeId && lastTradeDocIdRef.current !== actualTradeId) {
            lastTradeDocIdRef.current = actualTradeId;

            try {
              tradeUnsub?.();
            } catch {}

            tradeUnsub = db
              .collection("Trades")
              .doc(actualTradeId)
              .onSnapshot(
                (tSnap) => setTrade(tSnap.exists ? { id: tSnap.id, ...tSnap.data() } : null),
                (e) => console.warn("Trade snapshot error:", e)
              );
          } else if (!actualTradeId) {
            setTrade(null);
          }

          if (lastLogisticsDocIdRef.current !== tradeId) {
            lastLogisticsDocIdRef.current = tradeId;

            try {
              logisticsUnsub?.();
            } catch {}

            logisticsUnsub = db
              .collection("TradeLogistics")
              .doc(tradeId)
              .onSnapshot(
                (lSnap) => setLogistics(lSnap.exists ? { id: lSnap.id, ...lSnap.data() } : null),
                (e) => console.warn("TradeLogistics snapshot error:", e)
              );
          }
        },
        (e) => {
          console.error("WF snapshot error:", e);
          setErr("Failed to load workflow.");
          setLoading(false);
        }
      );

    return () => {
      try {
        wfUnsub();
      } catch {}
      try {
        tradeUnsub?.();
      } catch {}
      try {
        logisticsUnsub?.();
      } catch {}
      lastTradeDocIdRef.current = "";
      lastLogisticsDocIdRef.current = "";
    };
  }, [tradeId]);

  useEffect(() => {
    if (!tradeId) return;

    setDropoffPartnersLoading(true);
    const unsubscribe = db.collection("approvedPartners").onSnapshot(
      (snap) => {
        const partners = [];

        snap.forEach((doc) => {
          const p = { id: doc.id, ...(doc.data() || {}) };
          const active =
            p.isActive === true || p.active === true || String(p.status || "").toLowerCase() === "active";
          if (!active) return;

          partners.push({
            id: p.id,
            name: p.name || p.partnerName || "Drop-off Partner",
            address: p.address || p.location?.address || "",
            city: p.city || p.location?.city || "",
            province: p.province || p.location?.province || "",
            contactPhone: p.contactPhone || p.phone || "",
            operatingHours: p.operatingHours || "",
          });
        });

        partners.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" }));
        setDropoffPartners(partners);
        setDropoffPartnersLoading(false);
      },
      (error) => {
        console.warn("Failed to load drop-off partners:", error);
        setDropoffPartners([]);
        setDropoffPartnersLoading(false);
      }
    );

    return () => {
      try {
        unsubscribe();
      } catch {}
    };
  }, [tradeId]);

  const stageRaw = wf?.stage || "";
  const stage = stageAlias(stageRaw);
  const wfId = wf?.id || "";

  const sellerUid =
  wf?.sellerUid ||
  wf?.seller?.uid ||
  trade?.sellerUid ||
  trade?.ownerUid ||
  trade?.ownerId ||
  "";

const buyerUid =
  wf?.buyerUid ||
  wf?.buyer?.uid ||
  trade?.buyerUid ||
  "";

const isSeller = !!uid && !!sellerUid && uid === sellerUid;
const isBuyer = !!uid && !!buyerUid && uid === buyerUid;

  const canViewWorkflow =
    isAdmin ||
    canApproveSellerPrepPerm ||
    canReviewInspectionPerm ||
    canManagePickupPerm ||
    canManageDeliveryPerm ||
    canManageTradeIssuesPerm ||
    isSeller ||
    isBuyer;

  const tradeType = wf?.tradeType || wf?.paymentType || "";
  const isCashTrade = lower(tradeType) === "cash";

  const paymentStatus = lower(wf?.payment?.status || "");
  const cashStatusLegacy = lower(wf?.cash?.status || "");
  const resolvedPayStatus = paymentStatus || cashStatusLegacy || "pending";

  const isPaid = ["paid", "secured", "confirmed", "settled"].includes(resolvedPayStatus);
  const isPaymentPending = resolvedPayStatus === "pending";
  const isPaymentFailed = resolvedPayStatus === "failed";
  const isPaymentCancelled = resolvedPayStatus === "cancelled";

  const sellerPrepStatus = lower(wf?.sellerPrep?.status || "");
  const buyerApprovalStatus = lower(wf?.buyerApproval?.status || "");

  const inspectionFeeStatus = lower(wf?.inspectionFee?.status || "");
  const inspectionFeeAmount =
    wf?.inspectionFee?.amount ??
    wf?.inspectionFee?.amountZar ??
    wf?.inspectionFee?.sellerInspectionFeeZar ??
    null;

  const INSPECTION_FEE_CLEARED_STATUSES = new Set([
    "paid",
    "waived",
    "not_required",
    "not required",
    "not-required",
  ]);

  const INSPECTION_FEE_PAYABLE_STATUSES = new Set([
    "",
    "pending",
    "payable",
    "unpaid",
    "created",
    "requires_payment",
  ]);

  const isInspectionFeePaid = inspectionFeeStatus === "paid";
  const isInspectionFeeWaived = inspectionFeeStatus === "waived";
  const isInspectionFeeCleared = INSPECTION_FEE_CLEARED_STATUSES.has(inspectionFeeStatus);

  const isInspectionFeePending =
    INSPECTION_FEE_PAYABLE_STATUSES.has(inspectionFeeStatus) &&
    !isInspectionFeeCleared;

  const fulfillmentMethod = lower(wf?.fulfillmentChoice?.method || "");
  const dropoffPartnerId = lower(wf?.dropoff?.partnerId || "");

  const isPartnerDropoff =
    fulfillmentMethod === "partner_dropoff" ||
    dropoffPartnerId === "approved_dropoff_point" ||
    stage === "awaiting_dropoff";

  const isPlatformPickup = !isPartnerDropoff;

  const platformPickupInspectionFeeReady =
  !isCashTrade || (isPaid && isInspectionFeePaid);

  const partnerDropoffInspectionFeeReady =
    !isCashTrade || isPaid;

  const dropoff = wf?.dropoff || {};
  const dropoffStatus = lower(dropoff?.status || "");

  const verification = wf?.verification || {};
  const verificationStatus = lower(verification?.status || "");
  const verificationWindowEndsAt = verification?.windowEndsAt || null;

  const inspectionContext = useMemo(
    () => getInspectionContext({ stage, isPartnerDropoff }),
    [stage, isPartnerDropoff]
  );

  const inspectionAccessUrl = useMemo(
    () => safeStr(wf?.inspectionAccess?.lastIssuedUrl),
    [wf]
  );

  const inspectionQrImageUrl = useMemo(() => {
    if (!inspectionAccessUrl) return "";
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
      inspectionAccessUrl
    )}`;
  }, [inspectionAccessUrl]);

  const inspectionAccess = wf?.inspectionAccess || {};

  const inspectionAccessStatus = useMemo(() => {
    const raw =
      safeStr(inspectionAccess?.tokenStatus) ||
      safeStr(inspectionAccess?.status) ||
      "unknown";
    return lower(raw);
  }, [inspectionAccess]);

  const inspectionAccessExpiresAt = useMemo(() => {
    return inspectionAccess?.expiresAtIso || null;
  }, [inspectionAccess]);

  const inspectionAccessExpired = useMemo(() => {
    if (!inspectionAccessExpiresAt) return false;
    const t = new Date(inspectionAccessExpiresAt).getTime();
    return Number.isFinite(t) ? Date.now() > t : false;
  }, [inspectionAccessExpiresAt]);

  const inspectionAccessDisplayStatus = useMemo(() => {
    if (inspectionAccessExpired && inspectionAccessStatus === "active") return "expired";
    return inspectionAccessStatus;
  }, [inspectionAccessExpired, inspectionAccessStatus]);

  useEffect(() => {
    if (!wf) return;

    if (isSubmittedOrApproved(sellerPrepStatus)) {
      setJustSubmitted(true);
      setSubmitting(false);
      setUploadPct(100);
      setUploadLabel("Item confirmed. Awaiting review.");
    }
  }, [wf, sellerPrepStatus]);

  useEffect(() => {
    if (buyerApprovalStatus === "approved") setBuyerJustApproved(true);
  }, [buyerApprovalStatus]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const sp = wf?.sellerPrep || {};
      const rawPhotos = Array.isArray(sp.photos) ? sp.photos : [];
      const rawVideos = Array.isArray(sp.videos) ? sp.videos : [];

      const photosIn = uniqArr(rawPhotos);
      const videosIn = uniqArr(rawVideos);

      const resolvedPhotos = (await Promise.all(photosIn.map(toDownloadUrl))).filter(Boolean);
      const resolvedVideos = (await Promise.all(videosIn.map(toDownloadUrl))).filter(Boolean);

      if (cancelled) return;

      setEvidence({
        photos: uniqArr(resolvedPhotos),
        videos: uniqArr(resolvedVideos),
        notes: sp.notes || sp.approvalNotes || "",
        conditionGrade: sp.conditionGrade || wf?.condition?.conditionGrade || "",
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [wf]);

  useEffect(() => {
    const next = (photoFiles || []).map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));

    setPhotoPreviewUrls(next);

    return () => {
      next.forEach((item) => {
        try {
          URL.revokeObjectURL(item.url);
        } catch {}
      });
    };
  }, [photoFiles]);

  useEffect(() => {
    const next = (videoFiles || []).map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));

    setVideoPreviewUrls(next);

    return () => {
      next.forEach((item) => {
        try {
          URL.revokeObjectURL(item.url);
        } catch {}
      });
    };
  }, [videoFiles]);

  const dropoffOptions = useMemo(() => {
    return dropoffPartners;
  }, [dropoffPartners]);

  const selectedDropoffOption = useMemo(() => {
    return dropoffOptions.find((p) => p.id === selectedDropoffPartnerId) || null;
  }, [dropoffOptions, selectedDropoffPartnerId]);

  const idx = useMemo(() => ORDER.indexOf(stage), [stage]);
  const progressPct = useMemo(() => {
    if (idx < 0 || ORDER.length <= 1) return 0;
    return Math.round((idx / (ORDER.length - 1)) * 100);
  }, [idx]);

  const timeline = useMemo(() => {
    return ORDER.map((s, i) => ({
      key: s,
      label: STAGE_LABELS[s] || s,
      owner: getStageOwner(s, {
        isPartnerDropoff,
        isPlatformPickup,
      }),
      state: stage === s ? "current" : idx >= 0 && i < idx ? "done" : "todo",
    }));
  }, [stage, idx, isPartnerDropoff, isPlatformPickup]);

const userPhaseKey = useMemo(() => getUserPhaseKey(stage), [stage]);

const userPhaseIndex = useMemo(() => {
  const found = USER_PHASES.findIndex((p) => p.key === userPhaseKey);
  return found >= 0 ? found : 0;
}, [userPhaseKey]);

const userTimeline = useMemo(() => {
  return USER_PHASES.map((phase, i) => ({
    key: phase.key,
    label: phase.label,
    state:
      i < userPhaseIndex
        ? "done"
        : i === userPhaseIndex
        ? "current"
        : "todo",
  }));
}, [userPhaseIndex]);

  const call = useMemo(() => {
    return {
      submitSellerPrep: functions.httpsCallable("submitSellerPrep"),
      approveSellerPrepAdmin: functions.httpsCallable("approveSellerPrepAdmin"),
      buyerApproveSellerPrep: functions.httpsCallable("buyerApproveCashPrep"),
      buyerDeclineSellerPrep: functions.httpsCallable("buyerDeclineSellerPrep"),
      chooseInspectionMethod: functions.httpsCallable("chooseInspectionMethod"),
      issueInspectionAccess: functions.httpsCallable("issueInspectionAccess"),
      submitPickupInspection: functions.httpsCallable("submitPickupInspection"),
      schedulePickup: functions.httpsCallable("schedulePickup"),
      startPickup: functions.httpsCallable("startPickup"),
      submitHubInspection: functions.httpsCallable("submitHubInspection"),
      scheduleDelivery: functions.httpsCallable("scheduleDelivery"),
      markDelivered: functions.httpsCallable("markDelivered"),
      buyerConfirmDelivery: functions.httpsCallable("buyerConfirmDelivery"),
      completeTrade: functions.httpsCallable("completeTrade"),
      buyerReportIssue: functions.httpsCallable("buyerReportIssue"),
      createInspectionFeePayment: functions.httpsCallable("createInspectionFeePayment"),
    };
  }, []);

  const toggleChecklist = (k) => setChecklist((p) => ({ ...p, [k]: !p[k] }));

  const localPhotoCount = photoFiles.length || 0;
  const localVideoCount = videoFiles.length || 0;
  const remotePhotoCount = evidence.photos.length || 0;
  const remoteVideoCount = evidence.videos.length || 0;

  const requiresFullSellerPrep = useMemo(() => {
  const explicitRequired =
    wf?.sellerPrep?.requiresFullPrep === true ||
    wf?.sellerPrep?.mode === "full_evidence" ||
    wf?.prepMode === "full_evidence";

  const riskLevel = lower(
    wf?.risk?.level ||
      wf?.riskLevel ||
      trade?.riskLevel ||
      trade?.moderation?.riskLevel ||
      ""
  );

  const categoryKey = lower(
    trade?.categoryKey ||
      trade?.category ||
      trade?.categoryLabel ||
      wf?.categoryKey ||
      ""
  ).replace(/\s+/g, "_");

  const acceptedAmount = toMoneyNumber(
    wf?.cash?.amountGross ??
      wf?.cashDeal?.amountZar ??
      wf?.cashDeal?.itemAmountZar ??
      trade?.price ??
      trade?.cashPrice ??
      trade?.estimatedValue
  );

  return (
    explicitRequired ||
    riskLevel === "high" ||
    acceptedAmount >= 5000 ||
    HIGH_RISK_CATEGORY_KEYS.has(categoryKey)
  );
}, [wf, trade]);

  // MERGED COUNTS (local + remote combined)
  const effectivePhotoCount =
    (evidence.photos?.length || 0) + (photoFiles?.length || 0);

  const effectiveVideoCount =
    (evidence.videos?.length || 0) + (videoFiles?.length || 0);

  // SINGLE SOURCE OF TRUTH
  // Normal inspected trades use light confirmation.
  // High-risk trades still require full evidence.
  const sellerPrepReady = requiresFullSellerPrep
    ? effectivePhotoCount >= 4 && effectiveVideoCount >= 1
    : true;

   useEffect(() => {
    if (effectivePhotoCount >= 4 && missingMediaShake.photos) {
      setMissingMediaShake((prev) => ({ ...prev, photos: false }));
    }
  }, [effectivePhotoCount, missingMediaShake.photos]);

  useEffect(() => {
    if (effectiveVideoCount >= 1 && missingMediaShake.videos) {
      setMissingMediaShake((prev) => ({ ...prev, videos: false }));
    }
  }, [effectiveVideoCount, missingMediaShake.videos]);

  const photoProgress = Math.min(100, Math.round((effectivePhotoCount / 4) * 100));
  const videoProgress = Math.min(100, Math.round((effectiveVideoCount / 1) * 100));
  const sellerPrepProgress = requiresFullSellerPrep
  ? Math.round((photoProgress + videoProgress) / 2)
  : 100;

  const missingPhotos = Math.max(0, 4 - effectivePhotoCount);
  const missingVideos = Math.max(0, 1 - effectiveVideoCount);

  const isSubmitted = isSubmittedOrApproved(sellerPrepStatus) || justSubmitted;
  const spSubmitted = isSubmittedOrApproved(sellerPrepStatus);
  const spApproved = sellerPrepStatus === "approved";
  const buyerApproved = buyerApprovalStatus === "approved" || buyerJustApproved;

  const hasEvidence = (evidence.photos?.length || 0) > 0 || (evidence.videos?.length || 0) > 0;

 
  const canSeeDropoffSensitiveDetails =
  canSeeAdminSurface || canReviewInspectionPerm || isSeller;

  const pickup = logistics?.pickup || {};
  const pickupStatus = lower(pickup?.status || "");
  const pickupWindowStartOut = pickup?.windowStart || logistics?.pickupLegs?.primary?.windowStart;
  const pickupWindowEndOut = pickup?.windowEnd || logistics?.pickupLegs?.primary?.windowEnd;

  const delivery = logistics?.delivery || {};
  const deliveryWindowStartOut = delivery?.windowStart || logistics?.deliveryLegs?.primary?.windowStart;
  const deliveryWindowEndOut = delivery?.windowEnd || logistics?.deliveryLegs?.primary?.windowEnd;

  const paymentPillText = (() => {
    if (!isCashTrade) return `Trade type: ${tradeType || "standard"}`;
    if (isPaid) return "Payment: Confirmed";
    if (isPaymentFailed) return "Payment: Failed";
    if (isPaymentCancelled) return "Payment: Cancelled";
    if (isPaymentPending) return "Payment: Pending";
    return "Payment: Pending";
  })();

  const paymentPillClass = (() => {
    if (!isCashTrade) return "subtle";
    if (isPaid) return "ok";
    if (isPaymentFailed || isPaymentCancelled) return "warn";
    return "warn";
  })();

  const routePillText = !fulfillmentMethod
  ? "Route: Not selected"
  : isPartnerDropoff
  ? "Route: Approved Drop-off Point"
  : "Route: Platform Pickup";

  const paymentStatusText = isPaid
    ? "Confirmed"
    : isPaymentFailed
    ? "Failed"
    : isPaymentCancelled
    ? "Cancelled"
    : "Pending";

  // --------------------------------------------------------
  // ACTIVE ACTION LOGIC — only one action section at a time
  // --------------------------------------------------------
  const activeActionKey = useMemo(() => {
    if (isSellerOfferMode) return null;

    // seller flow
    if (canSeeSellerOrBuyerWorkflow && stage === "seller_prep" && isSeller && !isSubmitted) {
      return "seller_prep_submit";
    }

    if (
      canSeeAdminSurface &&
      (stage === "seller_prep" || stage === "buyer_approval") &&
      canApproveSellerPrepPerm &&
      sellerPrepStatus === "submitted"
    ) {
      return "admin_review_seller_prep";
    }

    if (
      canSeeSellerOrBuyerWorkflow &&
      stage === "buyer_approval" &&
      isBuyer &&
      isCashTrade &&
      spApproved &&
      !buyerApproved
    ) {
      return "buyer_commitment";
    }

    if (
      canSeeSellerOrBuyerWorkflow &&
      isBuyer &&
      stage === "awaiting_payment"
    ) {
      return "buyer_payment";
    }

    if (
      canSeeSellerOrBuyerWorkflow &&
      isSeller &&
      stage === "ready_for_inspection" &&
      isCashTrade &&
      isPaid &&
      !fulfillmentMethod
    ) {
      return "choose_inspection_method";
    }

    if (
      canSeeSellerOrBuyerWorkflow &&
      isSeller &&
      stage === "ready_for_inspection" &&
      isPlatformPickup &&
      isCashTrade &&
      isPaid &&
      isInspectionFeePending
    ) {
      return "pay_inspection_fee";
    }

    if (
      (canReviewInspectionPerm || canSeeAdminSurface) &&

      (
        (stage === "awaiting_dropoff" && isPartnerDropoff && (!isCashTrade || isPaid)) ||
        (stage === "pickup_scheduled" && isPlatformPickup)
      )
    ) {
      return "submit_pickup_inspection";
    }

    if (
        canSeeAdminSurface &&
        canManagePickupPerm &&
        (isPlatformPickup && stage === "ready_for_inspection" && platformPickupInspectionFeeReady)
      ) {
        return "schedule_pickup";
    }

    if (
      canSeeAdminSurface &&
      canManageDeliveryPerm &&
      isPartnerDropoff &&
      stage === "inspection_passed"
    ) {
      return "schedule_delivery";
    }

    if (
      canSeeAdminSurface &&
      canManagePickupPerm &&
      isPlatformPickup &&
      stage === "inspection_passed"
    ) {
      return "start_pickup";
    }

    if (
      canSeeAdminSurface &&
      canManageDeliveryPerm &&
      stage === "pickup_in_progress"
    ) {
      return "schedule_delivery";
    }

    if (
      canSeeAdminSurface &&
      canManageDeliveryPerm &&
      stage === "delivery_scheduled"
    ) {
      return "mark_delivered";
    }

    if (
      canSeeSellerOrBuyerWorkflow &&
      isBuyer &&
      stage === "delivered_pending_buyer_verification"
    ) {
      return "buyer_review_window";
    }

    if (
      canSeeAdminSurface &&
      canManageTradeIssuesPerm &&
      stage === "verification_passed"
    ) {
      return "complete_trade";
    }

    return null;
  }, [
    isSellerOfferMode,
    canSeeSellerOrBuyerWorkflow,
    canSeeAdminSurface,
    canApproveSellerPrepPerm,
    canReviewInspectionPerm,
    canManagePickupPerm,
    canRunHubInspectionPerm,
    canManageDeliveryPerm,
    canManageTradeIssuesPerm,
    stage,
    isSeller,
    isBuyer,
    isAdmin,
    isAdminMode,
    isFullWorkflowMode,
    sellerPrepStatus,
    isSubmitted,
    spApproved,
    buyerApproved,
    isCashTrade,
    isPaid,
    fulfillmentMethod,
    isPlatformPickup,
    isInspectionFeePending,
    isInspectionFeePaid,
    isPartnerDropoff,
    platformPickupInspectionFeeReady,
    partnerDropoffInspectionFeeReady,
  ]);

  const activeActionRef = useMemo(() => {
    switch (activeActionKey) {
      case "seller_prep_submit":
        return sellerPrepRef;
      case "admin_review_seller_prep":
        return adminReviewRef;
      case "buyer_commitment":
      case "buyer_payment":
        return buyerApprovalRef;
      case "choose_inspection_method":
        return inspectionMethodRef;
      case "pay_inspection_fee":
        return inspectionFeeRef;
      case "submit_pickup_inspection":
        return adminPickupInspectionRef;
      case "schedule_pickup":
        return adminSchedulePickupRef;
      case "start_pickup":
        return adminStartPickupRef;
      case "schedule_delivery":
        return adminScheduleDeliveryRef;
      case "mark_delivered":
        return adminScheduleDeliveryRef;
      case "buyer_review_window":
        return buyerReviewRef;
      case "complete_trade":
        return adminCompleteRef;
      default:
        return actionsTopRef;
    }
  }, [activeActionKey]);

  useEffect(() => {
    didAutoScrollRef.current = false;
  }, [
    stage,
    mode,
    sellerPrepStatus,
    buyerApprovalStatus,
    activeActionKey,
    pickupBusy,
    startPickupBusy,
    pickupInspectionBusy,
    inspectionFeePayBusy,
    inspectionMethodBusy,
    deliveryBusy,
    markDeliveredBusy,
    buyerIssueBusy,
    buyerConfirmBusy,
  ]);

  useEffect(() => {
    if (loading || !wf || didAutoScrollRef.current) return;
    const target = activeActionRef?.current || actionsTopRef.current;
    if (!target) return;

    didAutoScrollRef.current = true;
    const t = setTimeout(() => {
      try {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch {}
    }, 80);

    return () => clearTimeout(t);
  }, [loading, wf, activeActionRef]);

  useEffect(() => {
    return () => {
      try {
        clearTimeout(uploadCloseTimerRef.current);
      } catch {}
    };
  }, []);

 const triggerMissingMediaFeedback = useCallback(() => {
  const needPhotos = effectivePhotoCount < 4;
  const needVideos = effectiveVideoCount < 1;

  if (!needPhotos && !needVideos) return;

  setMissingMediaShake({
    photos: needPhotos,
    videos: needVideos,
  });

  if (needPhotos && photoInputRef.current) {
    try {
      photoInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch {}
  } else if (needVideos && videoInputRef.current) {
    try {
      videoInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch {}
  }

  setTimeout(() => {
    setMissingMediaShake({ photos: false, videos: false });
  }, 700);
}, [effectivePhotoCount, effectiveVideoCount]);

   const removePhotoFileAt = useCallback((idx) => {
    setPhotoFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const removeVideoFileAt = useCallback((idx) => {
    setVideoFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);


  const submitSellerPrep = useCallback(async () => {
    setErr("");
    setUploadErr("");
    setJustSubmitted(false);

    if (!isSeller) return setErr("Only the seller can submit seller prep.");

    if (requiresFullSellerPrep && effectivePhotoCount < 4) {
      triggerMissingMediaFeedback();
      return setErr("This trade requires at least 4 photos before you can confirm the item.");
    }

    if (requiresFullSellerPrep && effectiveVideoCount < 1) {
      triggerMissingMediaFeedback();
      return setErr("This trade requires at least 1 video before you can confirm the item.");
    }

    setSubmitting(true);
    setUploadOpen(true);
    setUploadPct(0);
    setUploadLabel("Starting…");

    try {
      if (!wfId) {
        const msg = "Workflow not ready. Please refresh and try again.";
        setErr(msg);
        setUploadErr(msg);
        setUploadLabel(msg);
        setUploadPct(0);
        return;
      }

      const basePath = `tradeMedia/${wfId}/sellerPrep`;

      const uploadedPhotos = await uploadMany(photoFiles, `${basePath}/photos`, currentUser, (p) => {
        setUploadPct(p.overallPct);
        setUploadLabel(`Photos ${p.index}/${p.total}: ${p.name} (${p.filePct}%)`);
      });

      const uploadedVideos = await uploadMany(videoFiles, `${basePath}/videos`, currentUser, (p) => {
        setUploadPct(p.overallPct);
        setUploadLabel(`Videos ${p.index}/${p.total}: ${p.name} (${p.filePct}%)`);
      });

     const photos = uniqArr([...(evidence.photos || []), ...uploadedPhotos.map((x) => x.url)]);
     const videos = uniqArr([...(evidence.videos || []), ...uploadedVideos.map((x) => x.url)]);

      setUploadLabel("Finalizing submission…");
      setUploadPct(100);

      await call.submitSellerPrep({
        tradeId,
        checklist,
        photos,
        videos,
        conditionGrade,
        notes: notes || "",
        prepMode: requiresFullSellerPrep ? "full_evidence" : "light_confirmation",
      });

      setJustSubmitted(true);
      setUploadErr("");
      setUploadLabel("Item confirmed. Awaiting review.");
      setUploadPct(100);
      setPhotoFiles([]);
      setVideoFiles([]);

      try {
        clearTimeout(uploadCloseTimerRef.current);
      } catch {}
      uploadCloseTimerRef.current = setTimeout(() => setUploadOpen(false), 600);
    } catch (e) {
      console.error(e);
      const msg = e?.message || "Seller prep submission failed.";
      setErr(msg);
      setUploadErr(msg);
    } finally {
      setSubmitting(false);
    }
  }, [
    tradeId,
    checklist,
    photoFiles,
    videoFiles,
    conditionGrade,
    notes,
    call,
    wfId,
    currentUser,
    localPhotoCount,
    localVideoCount,
    remotePhotoCount,
    remoteVideoCount,
    isSeller,
    triggerMissingMediaFeedback,
    evidence.photos,
    evidence.videos,
    effectivePhotoCount,
    effectiveVideoCount,
    requiresFullSellerPrep,
  ]);

 
  const approveSellerPrepAdmin = useCallback(async () => {
    setErr("");
    if (!canApproveSellerPrepPerm) return setErr("You do not have permission to approve seller prep.");

    setAdminApprovalBusy(true);
    try {
      await call.approveSellerPrepAdmin({ tradeId });
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Admin approval failed.");
    } finally {
      setAdminApprovalBusy(false);
    }
  }, [tradeId, canApproveSellerPrepPerm, call]);

  const buyerApproveSellerPrep = useCallback(async () => {
    setErr("");
    if (!isBuyer) return setErr("Only the buyer can commit to proceed.");

    setBuyerApprovalBusy(true);
    try {
      await call.buyerApproveSellerPrep({ tradeId, notes: null });
      setBuyerJustApproved(true);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Buyer commitment failed.");
    } finally {
      setBuyerApprovalBusy(false);
    }
  }, [tradeId, call, isBuyer]);

  const buyerDeclineSellerPrep = useCallback(async () => {
    setErr("");
    if (!isBuyer) return setErr("Only the buyer can decline.");

    const details = safeStr(buyerIssueNote);
    if (!details) return setErr("Please provide a short decline reason.");

    setBuyerIssueBusy(true);
    try {
      await call.buyerDeclineSellerPrep({
        tradeId,
        reasonCode: "not_satisfied",
        details,
      });
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Decline failed.");
    } finally {
      setBuyerIssueBusy(false);
    }
  }, [call, tradeId, buyerIssueNote, isBuyer]);

 const chooseInspectionMethod = useCallback(
    async (method) => {
      setErr("");

      if (!isSeller) {
        return setErr("Only the seller can choose the inspection method.");
      }

      if (stage !== "ready_for_inspection") {
        return setErr("Inspection method can only be chosen at ready_for_inspection.");
      }

      if (method === "partner_dropoff" && !selectedDropoffOption) {
        return setErr("Please select a drop-off partner first.");
      }

      setInspectionMethodBusy(true);

      try {
        const partner =
          method === "partner_dropoff" && selectedDropoffOption
            ? {
                partnerId: selectedDropoffOption.id,
                partnerName: selectedDropoffOption.name,
                address: selectedDropoffOption.address,
                city: selectedDropoffOption.city,
                province: selectedDropoffOption.province || "",
                contactPhone: selectedDropoffOption.contactPhone || "",
                operatingHours: selectedDropoffOption.operatingHours || "",
              }
            : null;

        await call.chooseInspectionMethod({
          tradeId,
          method,
          ...(partner || {}),
        });

        if (method === "partner_dropoff") {
          await call.issueInspectionAccess({
            tradeId,
            inspectionType: "partner_dropoff_intake",
            expiresInHours: 72,
            singleUse: true,
            baseUrl: window.location.origin,
            routePath: "/inspection/scan",
            note: "Auto-issued after partner drop-off selection",
          });
        }
      } catch (e) {
        console.error(e);
        setErr(
          e?.message ||
            (method === "partner_dropoff"
              ? "Failed to select drop-off partner or issue inspection access."
              : "Failed to choose inspection method.")
        );
      } finally {
        setInspectionMethodBusy(false);
      }
    },
    [isSeller, stage, call, tradeId, selectedDropoffOption]
  );

  const toBase64UrlUtf8 = (obj) => {
    const json = JSON.stringify(obj || {});
    const b64 = btoa(unescape(encodeURIComponent(json)));
    return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
  };

  const startInspectionFeePayment = useCallback(async () => {
    setErr("");

   if (!isSeller) return setErr("Only the seller can pay the inspection fee.");
    if (stage !== "ready_for_inspection") return setErr("Inspection fee is not open yet.");
    if (!isCashTrade || !isPaid) return setErr("Buyer payment must be confirmed first.");

    if (!isPlatformPickup) {
      return setErr("Partner drop-off inspection fee is waived. No inspection payment is required.");
    }

    if (isInspectionFeeCleared) {
      return setErr("Inspection fee is already cleared.");
    }

    setInspectionFeePayBusy(true);
    try {
      const resp = await call.createInspectionFeePayment({ tradeId });
      const data = resp?.data || {};

      const payfastUrl = data.payfastUrl || data.pf || "";
      const fields = data.fields || null;
      const redirectUrl = data.redirectUrl || data.url || data.payfastRedirectUrl || "";

      if (payfastUrl && fields && typeof fields === "object") {
        const f = toBase64UrlUtf8(fields);
        navigate(
          `/pay/redirect?pf=${encodeURIComponent(String(payfastUrl))}` +
            `&f=${encodeURIComponent(String(f))}` +
            `&t=${encodeURIComponent(String(tradeId))}`
        );
        return;
      }

      if (!redirectUrl) {
        throw new Error("Missing PayFast redirect data.");
      }

      navigate(
        `/pay/redirect?u=${encodeURIComponent(String(redirectUrl))}` +
          `&t=${encodeURIComponent(String(tradeId))}`
      );
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Failed to start inspection fee payment.");
    } finally {
      setInspectionFeePayBusy(false); 

    }
  }, [isSeller, stage, isCashTrade, isPaid, isInspectionFeePaid, isPlatformPickup, isInspectionFeeCleared, call, tradeId, navigate]);

  const uploadPickupInspectionPhotos = useCallback(async () => {
    const files = Array.from(pickupInspectionPhotoFiles || []).slice(0, 20);
    if (!files.length) return [];

    if (!wfId) throw new Error("Workflow not ready. Please refresh and try again.");

    setPickupInspectionUploadOpen(true);
    setPickupInspectionUploadPct(0);
    setPickupInspectionUploadLabel("Uploading inspection photos…");

    const basePath = `tradeMedia/${wfId}/pickupInspection/photos`;

    const results = await uploadMany(files, basePath, currentUser, (p) => {
      setPickupInspectionUploadPct(p.overallPct);
      setPickupInspectionUploadLabel(`Photos ${p.index}/${p.total}: ${p.name} (${p.filePct}%)`);
    });

    setPickupInspectionUploadPct(100);
    setPickupInspectionUploadLabel("Upload complete.");

    return results.map((r) => r.url).slice(0, 20);
  }, [pickupInspectionPhotoFiles, wfId, currentUser]);

  const submitPickupInspection = useCallback(
    async (pass) => {
      setErr("");
      if (!canReviewInspectionPerm) return setErr("You do not have permission to submit inspections.");

      if (isCashTrade && isPlatformPickup && (!isPaid || !isInspectionFeePaid)) {
          return setErr(
            "Inspection cannot proceed until buyer payment and platform pickup inspection fee are confirmed."
          );
      }

        if (isCashTrade && isPartnerDropoff && !isPaid) {
          return setErr("Buyer payment must be confirmed before partner intake can proceed.");
        }

      setPickupInspectionBusy(true);

      try {
        const photoUrls = await uploadPickupInspectionPhotos();

        await call.submitPickupInspection({
          tradeId,
          pass: !!pass,
          serialMatch: !!pickupInspectionSerialMatch,
          conditionMatch: !!pickupInspectionConditionMatch,
          notes: pickupInspectionNotes || "",
          photos: photoUrls,
          inspectionType: inspectionContext.inspectionType,
          inspectionMode: inspectionContext.inspectionMode,
          inspectionAccessId: wf?.inspectionAccess?.accessId || null,
        });

        setPickupInspectionPhotoFiles([]);
      } catch (e) {
        console.error(e);
        setErr(e?.message || "Failed to submit inspection.");
      } finally {
        setPickupInspectionBusy(false);
      }
    },
    [
      canReviewInspectionPerm,
      isCashTrade,
      isPartnerDropoff,
      isPaid,
      isInspectionFeePaid,
      call,
      tradeId,
      pickupInspectionSerialMatch,
      pickupInspectionConditionMatch,
      pickupInspectionNotes,
      uploadPickupInspectionPhotos,
      inspectionContext,
      isPlatformPickup,
      wf,
    ]
  );

  const schedulePickup = useCallback(async () => {
    setErr("");
    if (!canManagePickupPerm) return setErr("You do not have permission to schedule pickup.");
    if (!pickupWindowStart || !pickupWindowEnd) return setErr("Please provide both window start and window end.");

    setPickupBusy(true);
    try {
      await call.schedulePickup({
        tradeId,
        windowStart: toIsoOrNull(pickupWindowStart),
        windowEnd: toIsoOrNull(pickupWindowEnd),
        agentUid: safeStr(pickupAgentUid),
        partnerRef: safeStr(pickupPartnerRef),
        note: safeStr(pickupNote),
      });
    } catch (e) {
      console.error(e);
      setErr(getFriendlyWorkflowError(e, "Failed to schedule pickup."));
    } finally {
      setPickupBusy(false);
    }
  }, [
    canManagePickupPerm,
    call,
    tradeId,
    pickupWindowStart,
    pickupWindowEnd,
    pickupAgentUid,
    pickupPartnerRef,
    pickupNote,
  ]);

  const startPickup = useCallback(async () => {
    setErr("");
    if (!canManagePickupPerm) return setErr("You do not have permission to start pickup.");

    setStartPickupBusy(true);
    try {
      await call.startPickup({ tradeId, note: safeStr(startPickupNote) });
    } catch (e) {
      console.error(e);
      setErr(getFriendlyWorkflowError(e, "Failed to start pickup."));
    } finally {
      setStartPickupBusy(false);
    }
  }, [canManagePickupPerm, call, tradeId, startPickupNote]);

  const submitHubInspection = useCallback(
    async (pass) => {
      setErr("");
      if (!canRunHubInspectionPerm) {
        return setErr("You do not have permission to submit hub inspection.");
      }

      setHubInspectionBusy(true);
      try {
        await call.submitHubInspection({
          tradeId,
          pass: !!pass,
          notes: safeStr(hubInspectionNote),
          photos: [],
        });
      } catch (e) {
        console.error(e);
        setErr(e?.message || "Failed to submit hub inspection.");
      } finally {
        setHubInspectionBusy(false);
      }
    },
    [canRunHubInspectionPerm, call, tradeId, hubInspectionNote]
  );

  const scheduleDelivery = useCallback(async () => {
    setErr("");
    if (!canManageDeliveryPerm) return setErr("You do not have permission to schedule delivery.");
    if (!deliveryWindowStart || !deliveryWindowEnd) return setErr("Please provide both delivery window start and end.");

    setDeliveryBusy(true);
    try {
      await call.scheduleDelivery({
        tradeId,
        windowStart: toIsoOrNull(deliveryWindowStart),
        windowEnd: toIsoOrNull(deliveryWindowEnd),
        partnerRef: safeStr(deliveryPartnerRef),
        note: safeStr(deliveryNote),
      });
    } catch (e) {
      console.error(e);
      setErr(getFriendlyWorkflowError(e, "Failed to schedule delivery."));
    } finally {
      setDeliveryBusy(false);
    }
  }, [canManageDeliveryPerm, call, tradeId, deliveryWindowStart, deliveryWindowEnd, deliveryPartnerRef, deliveryNote]);

  const markDelivered = useCallback(async () => {
    setErr("");
    setDeliveryUploadError("");
    if (!canManageDeliveryPerm) return setErr("You do not have permission to mark delivered.");
    if (!wfId) return setErr("Workflow not ready. Please refresh and try again.");

    const files = Array.from(deliveryPhotoFiles || [])
      .filter((f) => f?.type?.startsWith("image/"))
      .slice(0, 5);
    if (files.length === 0 && deliveryPhotos.length === 0) {
      return setErr("At least one delivery photograph is required.");
    }
    if (deliveryPhotos.length + files.length > 5) {
      return setErr("You may upload at most five delivery photographs.");
    }
    if (files.some((f) => f.size > 5 * 1024 * 1024)) {
      return setErr("Each delivery photograph must be under 5 MB.");
    }

    setMarkDeliveredBusy(true);
    let uploadedResults = [];
    try {
      if (files.length > 0) {
        setDeliveryUploadBusy(true);
        uploadedResults = await uploadMany(
          files,
          `tradeMedia/${wfId}/delivery/photos`,
          currentUser,
          () => {
            setDeliveryUploadError("");
          },
          { uploaderUid: currentUser?.uid }
        );
        setDeliveryPhotos((prev) => uniqArr([...prev, ...uploadedResults], (x) => x.path));
        setDeliveryPhotoFiles([]);
        setDeliveryUploadBusy(false);
      }

      const allPhotos = uniqArr([...deliveryPhotos, ...uploadedResults], (x) => x.path);
      if (allPhotos.length === 0) {
        return setErr("At least one delivery photograph is required.");
      }

      await call.markDelivered({ tradeId, photos: allPhotos.map((x) => x.path), notes: deliveryNote || "" });
      setDeliveryPhotos([]);
      setDeliveryPhotoFiles([]);
      setDeliveryNote("");
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Failed to mark delivered.");
      for (const r of uploadedResults) {
        try {
          await storage.ref(r.path).delete();
        } catch {
          console.error("Delivery photo cleanup failed");
        }
      }
    } finally {
      setMarkDeliveredBusy(false);
      setDeliveryUploadBusy(false);
    }
  }, [canManageDeliveryPerm, call, tradeId, wfId, deliveryPhotoFiles, deliveryPhotos, deliveryNote, currentUser]);

  const completeTrade = useCallback(async () => {
    setErr("");
    if (!canManageTradeIssuesPerm) return setErr("You do not have permission to complete this trade.");

    setCompleteBusy(true);
    try {
      await call.completeTrade({ tradeId });
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Failed to complete trade.");
    } finally {
      setCompleteBusy(false);
    }
  }, [canManageTradeIssuesPerm, call, tradeId]);

  const buyerConfirmDelivery = useCallback(async () => {
    setErr("");

    if (!isBuyer) return setErr("Only the buyer can confirm receipt.");
    if (stage !== "delivered_pending_buyer_verification") {
      return setErr("Receipt can only be confirmed after delivery handover.");
    }

    setBuyerConfirmBusy(true);

    try {
      await call.buyerConfirmDelivery({ tradeId });
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Failed to confirm receipt.");
    } finally {
      setBuyerConfirmBusy(false);
    }
  }, [call, tradeId, isBuyer, stage]);

  const buyerReportIssue = useCallback(async () => {
    setErr("");
    if (!isBuyer) return setErr("Only the buyer can report an issue.");
    if (!safeStr(buyerIssueReason)) return setErr("Please select a reason for the issue.");
    if (!safeStr(buyerIssueNote)) return setErr("Please provide issue details.");

    setBuyerIssueBusy(true);
    try {
      await call.buyerReportIssue({
        tradeId,
        reasonCode: buyerIssueReason,
        details: safeStr(buyerIssueNote),
      });
      setBuyerIssueNote("");
      setBuyerIssueReason("");
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Failed to report issue.");
    } finally {
      setBuyerIssueBusy(false);
    }
  }, [call, tradeId, buyerIssueReason, buyerIssueNote, isBuyer]);

  const roleLabel = isAdmin
    ? "Admin"
    : canReviewInspectionPerm
    ? "Inspector"
    : isSeller
    ? "Seller"
    : isBuyer
    ? "Buyer"
    : "Viewer";

    const currentStageOwner = useMemo(() => {
      return getStageOwner(stage, {
        isPartnerDropoff,
        isPlatformPickup,
      });
    }, [stage, isPartnerDropoff, isPlatformPickup]);

  const nextStepMessage = useMemo(() => {
  switch (stage) {
    case "seller_prep":
      if (!spSubmitted) {
        return "Confirm the item is available and upload the required proof before the buyer proceeds.";
      }
      if (spSubmitted && !spApproved) {
        return "Your item confirmation is under review.";
      }
      return "Buyer needs to confirm and pay.";

    case "buyer_approval":
      return buyerApproved
        ? "Payment is now available."
        : "Review the item details and decide whether to continue.";

    case "awaiting_payment":
      return "Complete payment to continue.";

   case "ready_for_inspection":
      if (!fulfillmentMethod) {
        return "Choose how the item will be inspected.";
      }

      if (isPartnerDropoff) {
        return "Partner drop-off selected. The inspection fee is waived. Drop the item off at the approved location.";
      }

      if (isPlatformPickup && isInspectionFeePending) {
        return "Platform pickup selected. Pay the inspection fee so NexterZone can schedule pickup and inspection.";
      }

      if (isPlatformPickup && isInspectionFeePaid) {
        return "Inspection fee confirmed. NexterZone operations will book the pickup next.";
      }

      return "NexterZone operations will guide the next inspection step.";

    case "awaiting_dropoff":
      return wf?.dropoff?.partnerName
        ? `Drop off the item at ${wf.dropoff.partnerName}. The partner/admin team will complete intake inspection after drop-off.`
        : "Drop off the item at the selected approved partner location. The partner/admin team will complete intake inspection after drop-off.";

    case "inspection_passed":
      return isPartnerDropoff
        ? "Collection will be booked next."
        : "Pickup will start next.";

    case "pickup_scheduled":
      return isPlatformPickup
        ? "Inspection will happen during the pickup window."
        : "Pickup will start from the approved drop-off point.";

   case "pickup_in_progress":
    return "The item has passed inspection and is now in handover. Delivery can be scheduled next.";

    case "delivery_scheduled":
      return "Delivery is booked. NexterZone will update the trade once the item is handed over.";

    case "delivered_pending_buyer_verification":
      return "Confirm that you received the item, or report an issue if something is wrong.";

    case "verification_passed":
      return "Receipt has been confirmed. The trade can now be reviewed and closed.";

    case "completed":
      return "Trade completed successfully.";

    case "cancelled":
      return "Trade cancelled.";

    case "dispute_open":
      return "An issue was reported and is being reviewed.";

    default:
      return "";
  }
}, [
  stage,
  spSubmitted,
  spApproved,
  buyerApproved,
  fulfillmentMethod,
  isPlatformPickup,
  isInspectionFeePending,
  isPartnerDropoff,
  isInspectionFeePaid,
  ]);

  const viewerActionContext = useMemo(() => {
    if (canSeeAdminSurface) {
      return {
        title: STAGE_LABELS[stage] || prettyKey(stage || "workflow"),
        kicker: "Current action",
        message: nextStepMessage || "Follow the next step to continue.",
        isWaiting: false,
      };
    }

    if (activeActionKey) {
      return {
        title: STAGE_LABELS[stage] || prettyKey(stage || "workflow"),
        kicker: "Your action required",
        message: nextStepMessage || "Please complete the next step to continue.",
        isWaiting: false,
      };
    }

    const waitForSeller = [
      "seller_prep",
      "ready_for_inspection",
    ].includes(stage);

    const waitForBuyer = [
      "buyer_approval",
      "awaiting_payment",
      "delivered_pending_buyer_verification",
    ].includes(stage);

    const waitForPlatform = [
      "awaiting_dropoff",
      "inspection_passed",
      "pickup_scheduled",
      "pickup_in_progress",
      "delivery_scheduled",
      "verification_passed",
    ].includes(stage);

    if (isBuyer && waitForSeller) {
      return {
        title: "Waiting for seller",
        kicker: "No action needed from you",
        message:
          "The seller needs to complete this step. You will be notified when it is your turn.",
        isWaiting: true,
      };
    }

    if (isSeller && waitForBuyer) {
      return {
        title: "Waiting for buyer",
        kicker: "No action needed from you",
        message:
          "The buyer needs to complete this step. You will be notified when it is your turn.",
        isWaiting: true,
      };
    }

    if (stage === "awaiting_dropoff" && isSeller) {
      return {
        title: "Drop off item",
        kicker: "Offline action needed",
        message:
          nextStepMessage ||
          "Drop off the item at the selected approved partner location. The partner/admin team will complete intake inspection after drop-off.",
        isWaiting: false,
      };
    }

    if (stage === "awaiting_dropoff" && isBuyer) {
      return {
        title: "Waiting for drop-off inspection",
        kicker: "No action needed from you",
        message:
          "The seller must drop off the item at the selected partner location. The partner/admin team will inspect it before delivery continues.",
        isWaiting: true,
      };
    }

    if ((isBuyer || isSeller) && waitForPlatform) {
      return {
        title: "Platform is handling this step",
        kicker: "No action needed from you",
        message:
          "NexterZone operations is handling the next step. You can safely wait for the next update.",
        isWaiting: true,
      };
    }

    return {
      title: STAGE_LABELS[stage] || prettyKey(stage || "workflow"),
      kicker: "Current status",
      message: nextStepMessage || "No action is required on your side right now.",
      isWaiting: true,
    };
  }, [
    canSeeAdminSurface,
    activeActionKey,
    stage,
    nextStepMessage,
    isBuyer,
    isSeller,
  ]);

  if (loading || (currentUser && accessLoading)) {
    return <div className="twf-card">Loading workflow…</div>;
  }

  if (currentUser && !hasAccessProfile) {
    return <div className="twf-card">Your access profile is not provisioned yet.</div>;
  }

  if (currentUser && !accountIsActive) {
    return <div className="twf-card">Your account is inactive. Please contact an administrator.</div>;
  }

  if (!wf) {
  const ownerUid = trade?.ownerUid || trade?.ownerId || trade?.sellerUid || "";
  const isOwner = !!uid && !!ownerUid && uid === ownerUid;

  return (
    <div className="twp-shell twp-shell--clean">
      <div className="twf-panel twf-panel--actions twf-panel--elevated">
        <div className="twf-panel-head twf-panel-head--compact">
          <div className="twf-panel-kicker">Listing status</div>
          <div className="twf-panel-title">Your trade is live</div>
          <div className="twf-panel-subtitle">
            {isOwner
              ? "Your listing has been created successfully. Workflow steps will appear once a buyer starts the process."
              : "This trade is available. Workflow steps will appear once an offer is accepted and the transaction starts."}
          </div>
        </div>

        <StatusBlock
          title="Current Status"
          hint="There is no active trade workflow yet."
        >
          <div>
            Listing: <span className="mono">{trade?.title || "Trade item"}</span>
          </div>
          <div style={{ marginTop: 6 }}>
            Status: <span className="mono">{trade?.status || "open"}</span>
          </div>
        </StatusBlock>
      </div>
    </div>
  );
}

 if (!canViewWorkflow) {
    return (
      <div className="twp-shell twp-shell--clean">
        <div className="twf-panel twf-panel--actions twf-panel--elevated">
          <div className="twf-panel-head twf-panel-head--compact">
            <div className="twf-panel-kicker">Workflow</div>
            <div className="twf-panel-title">Workflow not available</div>
            <div className="twf-panel-subtitle">
              This workflow is only visible to the trade participants and authorised operations users.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
  <div className="twp-shell twp-shell--clean">
    {err ? <div className="twf-error">{err}</div> : null}

    {uploadOpen ? (
      <div className="twf-upload-backdrop" role="dialog" aria-modal="true">
        <div className="twf-upload-modal">
          <div className="twf-upload-head">
            <div className="twf-upload-title">Submitting Item Confirmation</div>
            <button
              type="button"
              className="twf-btn ghost"
              onClick={() => setUploadOpen(false)}
              disabled={submitting && !uploadErr && !isSubmitted}
            >
              Close
            </button>
          </div>

          <div className="twf-upload-body">
            <div className="twf-upload-label">{uploadLabel || "Working…"}</div>
            <div className="twf-upload-bar">
              <div
                className="twf-upload-bar-fill"
                style={{ width: `${Math.max(0, Math.min(100, uploadPct))}%` }}
              />
            </div>
            <div className="twf-muted">{uploadPct}%</div>

            {uploadErr ? (
              <div className="twf-error" style={{ marginTop: 10 }}>
                {uploadErr}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    ) : null}

    {pickupInspectionUploadOpen ? (
      <div className="twf-upload-backdrop" role="dialog" aria-modal="true">
        <div className="twf-upload-modal">
          <div className="twf-upload-head">
            <div className="twf-upload-title">Uploading Inspection Photos</div>
            <button
              type="button"
              className="twf-btn ghost"
              onClick={() => setPickupInspectionUploadOpen(false)}
              disabled={pickupInspectionBusy}
            >
              Close
            </button>
          </div>

          <div className="twf-upload-body">
            <div className="twf-upload-label">
              {pickupInspectionUploadLabel || "Working…"}
            </div>
            <div className="twf-upload-bar">
              <div
                className="twf-upload-bar-fill"
                style={{
                  width: `${Math.max(0, Math.min(100, pickupInspectionUploadPct))}%`,
                }}
              />
            </div>
            <div className="twf-muted">{pickupInspectionUploadPct}%</div>
          </div>
        </div>
      </div>
    ) : null}

      <div className="twp-clean-top">
      {canSeeWorkflowSummary ? (
        <div className="twp-simple-phase-wrap">
          <PremiumStepRail timeline={canSeeAdminSurface ? timeline : userTimeline} />
        </div>
      ) : null}

      <div className="twp-clean-trade-card">
        <div className="twp-clean-trade-card__media">
          <img
            src={
              trade?.primaryImage ||
              trade?.coverImage ||
              trade?.thumbnailUrl ||
              trade?.image ||
              trade?.photos?.[0] ||
              trade?.images?.[0] ||
              evidence.photos?.[0] ||
              "/placeholder-image.png"
            }
            alt={trade?.title || "Trade item"}
          />
        </div>

        <div className="twp-clean-trade-card__content">
          <div className="twp-clean-trade-card__eyebrow">Trade</div>

          <h2 className="twp-clean-trade-card__title">
            {trade?.title || trade?.name || "Trade item"}
          </h2>

          <div className="twp-clean-trade-card__price-row">
            <div className="twp-clean-trade-card__price">
              {trade?.price != null
                ? `R${Number(trade.price).toLocaleString("en-ZA")}`
                : trade?.cashPrice != null
                ? `R${Number(trade.cashPrice).toLocaleString("en-ZA")}`
                : "Price not set"}
            </div>
          </div>

          <div className="twp-clean-trade-card__meta">
            <span className={`twf-pill ${paymentPillClass}`}>{paymentStatusText}</span>
            <span className="twf-pill subtle">{routePillText}</span>
            <span className="twf-pill subtle">Role: {roleLabel}</span>
          </div>
        </div>
      </div>

      <div className="twf-panel twf-panel--actions twf-panel--elevated" ref={actionsTopRef}>
        <div className="twf-panel-head twf-panel-head--compact">
          <div className="twf-panel-kicker">
            {viewerActionContext.kicker}
          </div>
          <div className="twf-panel-title">
            {viewerActionContext.title}
          </div>
          <div className="twf-panel-subtitle">
            {viewerActionContext.message}
          </div>
        </div>

       {activeActionKey === "seller_prep_submit" ? (
          <PanelSection title="Confirm Item" active innerRef={sellerPrepRef}>
            <div className="twf-sellerprep-hero">
              <div className="twf-sellerprep-hero__top">
                <div>
                  <div className="twf-sellerprep-hero__title">Confirm Your Item</div>
                  <div className="twf-sellerprep-hero__subtitle">
                   {requiresFullSellerPrep ? (
                      <>
                        This trade requires stronger proof. Upload <b>4 photos</b> and <b>1 video</b>
                        before the buyer proceeds. Physical inspection still happens later.
                      </>
                    ) : (
                      <>
                        Confirm that the item is available and the condition is accurate. Photos and
                        videos are optional because physical inspection still happens later.
                      </>
                    )}
                  </div>
                </div>

                <div className={`twf-sellerprep-badge ${sellerPrepReady ? "is-ready" : ""}`}>
                  {sellerPrepReady ? "Ready to submit" : `${sellerPrepProgress}% complete`}
                </div>
              </div>

              <div className="twf-sellerprep-progress-wrap">
                <div className="twf-sellerprep-progress-head">
                 
                  <span>Confirmation readiness</span>
                  <span>{sellerPrepProgress}%</span>
                </div>

                <div className="twf-sellerprep-progress">
                  <div
                    className={`twf-sellerprep-progress__fill ${sellerPrepReady ? "is-ready" : ""}`}
                    style={{ width: `${sellerPrepProgress}%` }}
                  />
                </div>
              </div>

              {requiresFullSellerPrep ? (

                <div className="twf-sellerprep-requirements">
                  <div
                    className={`twf-sellerprep-req ${
                      effectivePhotoCount >= 4 ? "is-done" : ""
                    } ${missingMediaShake.photos ? "is-shaking" : ""}`}
                  >
                    <div className="twf-sellerprep-req__label">Photos</div>
                    <div className="twf-sellerprep-req__value">{effectivePhotoCount}/4</div>
                    <div className="twf-sellerprep-mini-progress">
                      <div
                        className="twf-sellerprep-mini-progress__fill"
                        style={{ width: `${photoProgress}%` }}
                      />
                    </div>
                    <div className="twf-sellerprep-req__hint">
                      {effectivePhotoCount >= 4
                        ? "Requirement met"
                        : `${missingPhotos} more needed`}
                    </div>
                  </div>

                  <div
                    className={`twf-sellerprep-req ${
                      effectiveVideoCount >= 1 ? "is-done" : ""
                    } ${missingMediaShake.videos ? "is-shaking" : ""}`}
                  >
                    <div className="twf-sellerprep-req__label">Video</div>
                    <div className="twf-sellerprep-req__value">{effectiveVideoCount}/1</div>
                    <div className="twf-sellerprep-mini-progress">
                      <div
                        className="twf-sellerprep-mini-progress__fill"
                        style={{ width: `${videoProgress}%` }}
                      />
                    </div>
                    <div className="twf-sellerprep-req__hint">
                      {effectiveVideoCount >= 1
                        ? "Requirement met"
                        : `${missingVideos} more needed`}
                    </div>
                  </div>
                </div>

               ) : (
                <div className="twf-sellerprep-requirements">
                  <div className="twf-sellerprep-req is-done">
                    <div className="twf-sellerprep-req__label">Item availability</div>
                    <div className="twf-sellerprep-req__value">✓</div>
                    <div className="twf-sellerprep-req__hint">
                      Confirm the item is available and ready for inspection.
                    </div>
                  </div>

                  <div className="twf-sellerprep-req is-done">
                    <div className="twf-sellerprep-req__label">Inspection still required</div>
                    <div className="twf-sellerprep-req__value">✓</div>
                    <div className="twf-sellerprep-req__hint">
                      NexterZone will still verify the item before delivery.
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="twf-row">
              <label className="twf-label">Condition</label>
              <select
                className="twf-input"
                value={conditionGrade}
                onChange={(e) => setConditionGrade(e.target.value)}
                disabled={!isSeller}
              >
                <option value="new">brand new</option>
                <option value="like new">like new</option>
                <option value="good">good</option>
                <option value="fair">fair</option>
                <option value="needs repair">needs repair</option>
              </select>
            </div>

            <div className="twf-checklist">
              {Object.keys(checklist).map((k) => (
                <label key={k} className="twf-check">
                  <input
                    type="checkbox"
                    checked={!!checklist[k]}
                    onChange={() => toggleChecklist(k)}
                    disabled={!isSeller}
                  />
                  <span>{prettyKey(k)}</span>
                </label>
              ))}
            </div>

            <div className="twf-row">
              <label className="twf-label">Notes</label>
              <textarea
                className="twf-input"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={!isSeller}
              />
            </div>

            <div
              ref={photoInputRef}
              className={`twf-row twf-media-upload-card ${
                missingMediaShake.photos ? "twf-media-upload-card--shake twf-media-upload-card--error" : ""
              }`}
            >
              <label className="twf-label">Photos</label>
              <div className="twf-media-upload-subtext">
              
                {requiresFullSellerPrep
                  ? "Upload clear angles of the item. Minimum: 4 photos."
                  : "Optional: add photos if you want to provide extra proof."}
              </div>
              <input
                className="twf-input"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setPhotoFiles(Array.from(e.target.files || []))}
                disabled={!isSeller}
              />

              {(photoPreviewUrls.length > 0 || evidence.photos.length > 0) ? (
                <div className="twf-preview-grid">
                  {photoPreviewUrls.map((item, i) => (
                    <div className="twf-preview-card" key={`photo_local_${i}`}>
                      <img src={item.url} alt={`photo preview ${i + 1}`} />
                      <button
                        type="button"
                        className="twf-preview-remove"
                        onClick={() => removePhotoFileAt(i)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}

                  {photoPreviewUrls.length === 0 &&
                    evidence.photos.map((url, i) => (
                      <a
                        key={`photo_remote_${i}`}
                        className="twf-preview-card"
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img src={url} alt={`uploaded photo ${i + 1}`} />
                      </a>
                    ))}
                </div>
              ) : null}
            </div>

            <div
              ref={videoInputRef}
              className={`twf-row twf-media-upload-card ${
                missingMediaShake.videos ? "twf-media-upload-card--shake twf-media-upload-card--error" : ""
              }`}
            >
              <label className="twf-label">Video</label>
              <div className="twf-media-upload-subtext">
         
                {requiresFullSellerPrep
                  ? "Upload at least 1 video showing the item condition and functionality."
                  : "Optional: add a short video if it helps show the item condition."}
              </div>
              <input
                className="twf-input"
                type="file"
                accept="video/*"
                multiple={false}
                onChange={(e) => setVideoFiles(Array.from(e.target.files || []))}
                disabled={!isSeller}
              />

              {(videoPreviewUrls.length > 0 || evidence.videos.length > 0) ? (
                <div className="twf-preview-grid twf-preview-grid--video">
                  {videoPreviewUrls.map((item, i) => (
                    <div className="twf-preview-card twf-preview-card--video" key={`video_local_${i}`}>
                      <video src={item.url} controls preload="metadata" />
                      <button
                        type="button"
                        className="twf-preview-remove"
                        onClick={() => removeVideoFileAt(i)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}

                  {videoPreviewUrls.length === 0 &&
                    evidence.videos.map((url, i) => (
                      <div className="twf-preview-card twf-preview-card--video" key={`video_remote_${i}`}>
                        <video src={url} controls preload="metadata" />
                      </div>
                    ))}
                </div>
              ) : null}
            </div>

            <div className="twf-sellerprep-footer">
              <div className={`twf-sellerprep-status ${sellerPrepReady ? "is-ready" : "is-pending"}`}>
                {sellerPrepReady
                  ? requiresFullSellerPrep
                    ? "✓ Requirements met. You can now confirm the item."
                    : "✓ Ready. Confirm that the item is available and accurate."
                  : `Upload ${missingPhotos > 0 ? `${missingPhotos} more photo${missingPhotos > 1 ? "s" : ""}` : ""}${
                      missingPhotos > 0 && missingVideos > 0 ? " and " : ""
                    }${missingVideos > 0 ? `${missingVideos} video` : ""} to continue.`}
              </div>

              <button
                className="twf-btn twf-btn--primary"
                type="button"
                onClick={() => {
                  if (!sellerPrepReady) {
                    triggerMissingMediaFeedback();
                    return;
                  }
                  submitSellerPrep();
                }}
                disabled={submitting || !isSeller || isSubmitted}
              
                title={
                  !sellerPrepReady && requiresFullSellerPrep
                    ? "Requires at least 4 photos and 1 video"
                    : ""
                }
              >
                {submitting ? "Submitting…" : "Confirm Item"}
              </button>
            </div>
          </PanelSection>
        ) : null}

        {activeActionKey === "admin_review_seller_prep" ? (
          <PanelSection title="Review Seller Submission" active innerRef={adminReviewRef}>
            {evidence.photos.length ? (
              <div className="twf-media-grid" style={{ marginBottom: 12 }}>
                {evidence.photos.slice(0, 4).map((url, i) => (
                  <a
                    key={`adm_p_${i}`}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="twf-thumb"
                  >
                    <img src={url} alt={`evidence ${i + 1}`} />
                  </a>
                ))}
              </div>
            ) : null}

            <button
              className="twf-btn twf-btn--primary"
              type="button"
              onClick={approveSellerPrepAdmin}
              disabled={adminApprovalBusy}
            >
              {adminApprovalBusy ? "Approving…" : "Approve Submission"}
            </button>
          </PanelSection>
        ) : null}

        {activeActionKey === "buyer_commitment" ? (
          <PanelSection title="Confirm to Continue" active innerRef={buyerApprovalRef}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                className="twf-btn twf-btn--primary"
                type="button"
                onClick={buyerApproveSellerPrep}
                disabled={buyerApprovalBusy}
              >
                {buyerApprovalBusy ? "Confirming…" : "Continue"}
              </button>

              <button
                className="twf-btn twf-btn--danger"
                type="button"
                onClick={buyerDeclineSellerPrep}
                disabled={buyerIssueBusy}
              >
                {buyerIssueBusy ? "Declining…" : "Decline"}
              </button>
            </div>

            <div className="twf-row" style={{ marginTop: 12 }}>
              <label className="twf-label">Reason if declining</label>
              <textarea
                className="twf-input"
                rows={3}
                value={buyerIssueNote}
                onChange={(e) => setBuyerIssueNote(e.target.value)}
              />
            </div>
          </PanelSection>
        ) : null}

        {activeActionKey === "buyer_payment" ? (
          <PanelSection title="Make Payment" active innerRef={buyerApprovalRef}>
            <button
              type="button"
              className="twf-btn twf-btn--primary"
              onClick={() => navigate(`/checkout/${encodeURIComponent(tradeId)}`)}
            >
              Open Checkout
            </button>
          </PanelSection>
        ) : null}

        {activeActionKey === "choose_inspection_method" ? (
          <PanelSection title="Choose Inspection Option" active innerRef={inspectionMethodRef}>
            <div style={{ display: "grid", gap: 12 }}>
              {supportsPartnerDropoff(trade) ? (
                <div className="twf-muted">
                  Choose how the item should be inspected. Partner drop-off is free for the seller, but you must select the approved location where you will drop off the item.
                </div>
              ) : (
                <div className="twf-muted" style={{ color: "#ff9800", fontWeight: "500" }}>
                  This item is too large for partner drop-off locations. Only platform pickup is available for inspection.
                </div>
              )}

              <button
                type="button"
                className="twf-btn twf-btn--primary"
                onClick={() => chooseInspectionMethod("platform_pickup")}
                disabled={inspectionMethodBusy}
              >
                {inspectionMethodBusy ? "Saving…" : "Platform Pickup"}
              </button>

              {supportsPartnerDropoff(trade) ? (
                <>
                  <div className="twf-row">
                    <label className="twf-label" htmlFor="dropoff-partner-select">
                      Approved drop-off partner
                    </label>
                    <select
                      id="dropoff-partner-select"
                      className="twf-input"
                      value={selectedDropoffPartnerId}
                      onChange={(e) => setSelectedDropoffPartnerId(e.target.value)}
                      disabled={inspectionMethodBusy || dropoffPartnersLoading}
                    >
                      {dropoffPartnersLoading ? (
                        <option value="">Loading partners…</option>
                      ) : (
                        <option value="">Select a drop-off partner</option>
                      )}
                      {dropoffOptions.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                          {p.city ? ` — ${p.city}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  {!dropoffPartnersLoading && dropoffOptions.length === 0 ? (
                    <div className="twf-muted">
                      No active drop-off partners are currently available. Only platform pickup is available for this trade.
                    </div>
                  ) : (
                    <>
                      {selectedDropoffOption ? (
                        <div className="twf-section">
                          <div className="twf-section-title">Selected drop-off partner</div>
                          <div className="twf-muted">
                            <div>{selectedDropoffOption.name}</div>
                            <div>
                              {selectedDropoffOption.address}
                              {selectedDropoffOption.city ? `, ${selectedDropoffOption.city}` : ""}
                              {selectedDropoffOption.province ? `, ${selectedDropoffOption.province}` : ""}
                            </div>
                            {selectedDropoffOption.operatingHours ? (
                              <div>Hours: {selectedDropoffOption.operatingHours}</div>
                            ) : null}
                            {selectedDropoffOption.contactPhone ? (
                              <div>Phone: {selectedDropoffOption.contactPhone}</div>
                            ) : null}
                          </div>
                        </div>
                      ) : null}

                      <button
                        type="button"
                        className="twf-btn twf-btn--secondary"
                        onClick={() => chooseInspectionMethod("partner_dropoff")}
                        disabled={
                          inspectionMethodBusy ||
                          dropoffPartnersLoading ||
                          !selectedDropoffPartnerId
                        }
                      >
                        {inspectionMethodBusy ? "Saving…" : "Use Selected Drop-off Partner"}
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div className="twf-muted" style={{ paddingTop: 8, fontSize: "0.9em", color: "#999" }}>
                  Partner drop-off option is not available for this item size. Please proceed with platform pickup.
                </div>
              )}
            </div>
          </PanelSection>
        ) : null}

        {activeActionKey === "pay_inspection_fee" ? (
          <PanelSection title="Pay Inspection Fee" active innerRef={inspectionFeeRef}>
            <button
              type="button"
              className="twf-btn twf-btn--primary"
              onClick={startInspectionFeePayment}
              disabled={inspectionFeePayBusy}
            >
              {inspectionFeePayBusy ? "Opening payment…" : "Pay Inspection Fee"}
            </button>
          </PanelSection>
        ) : null}

        {activeActionKey === "submit_pickup_inspection" ? (
          <PanelSection title={inspectionContext.title} active innerRef={adminPickupInspectionRef}>
            <div className="twf-row">
              <label className="twf-label">Inspection notes</label>
              <textarea
                className="twf-input"
                rows={3}
                value={pickupInspectionNotes}
                onChange={(e) => setPickupInspectionNotes(e.target.value)}
              />
            </div>

            <div className="twf-row">
              <label className="twf-label">Photos</label>
              <input
                className="twf-input"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setPickupInspectionPhotoFiles(Array.from(e.target.files || []))}
              />
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                className="twf-btn twf-btn--primary"
                type="button"
                onClick={() => submitPickupInspection(true)}
                disabled={pickupInspectionBusy}
              >
                Pass
              </button>

              <button
                className="twf-btn twf-btn--danger"
                type="button"
                onClick={() => submitPickupInspection(false)}
                disabled={pickupInspectionBusy}
              >
                Fail
              </button>
            </div>
          </PanelSection>
        ) : null}

        {activeActionKey === "schedule_pickup" ? (
          <PanelSection title="Schedule Pickup" active innerRef={adminSchedulePickupRef}>
            <div className="twf-row">
              <label className="twf-label">Window start</label>
              <input
                className="twf-input"
                type="datetime-local"
                value={pickupWindowStart}
                onChange={(e) => setPickupWindowStart(e.target.value)}
              />
            </div>

            <div className="twf-row">
              <label className="twf-label">Window end</label>
              <input
                className="twf-input"
                type="datetime-local"
                value={pickupWindowEnd}
                onChange={(e) => setPickupWindowEnd(e.target.value)}
              />
            </div>

            <button
              className="twf-btn twf-btn--primary"
              type="button"
              onClick={schedulePickup}
              disabled={pickupBusy}
            >
              {pickupBusy ? "Scheduling…" : "Schedule Pickup"}
            </button>
          </PanelSection>
        ) : null}

        {isPartnerDropoff && stage === "pickup_scheduled" ? (
          <PanelSection title="Pickup not required" active>
            <div className="twf-hint" style={{ marginBottom: 12 }}>
              Pickup is not required for approved drop-off workflows. Continue by scheduling delivery.
            </div>
          </PanelSection>
        ) : null}

        {activeActionKey === "start_pickup" ? (
          <PanelSection title="Start Pickup" active innerRef={adminStartPickupRef}>
            <button
              className="twf-btn twf-btn--primary"
              type="button"
              onClick={startPickup}
              disabled={startPickupBusy}
            >
              {startPickupBusy ? "Starting…" : "Start Pickup"}
            </button>
          </PanelSection>
        ) : null}

        {activeActionKey === "schedule_delivery" ? (
          <PanelSection title="Schedule Delivery" active innerRef={adminScheduleDeliveryRef}>
            {isPartnerDropoff && stage === "inspection_passed" ? (
              <div className="twf-hint" style={{ marginBottom: 12 }}>
                The item has passed partner intake inspection. Schedule delivery to the buyer.
              </div>
            ) : null}

            <div className="twf-row">
              <label className="twf-label">Window start</label>
              <input
                className="twf-input"
                type="datetime-local"
                value={deliveryWindowStart}
                onChange={(e) => setDeliveryWindowStart(e.target.value)}
              />
            </div>

            <div className="twf-row">
              <label className="twf-label">Window end</label>
              <input
                className="twf-input"
                type="datetime-local"
                value={deliveryWindowEnd}
                onChange={(e) => setDeliveryWindowEnd(e.target.value)}
              />
            </div>

            <button
              className="twf-btn twf-btn--primary"
              type="button"
              onClick={scheduleDelivery}
              disabled={deliveryBusy}
            >
              {deliveryBusy ? "Scheduling…" : "Schedule Delivery"}
            </button>
          </PanelSection>
        ) : null}

        {activeActionKey === "mark_delivered" ? (
          <PanelSection title="Mark Delivery Handover" active innerRef={adminScheduleDeliveryRef}>
            <div className="twf-hint" style={{ marginBottom: 12 }}>
              Confirm only after the item has been physically handed over to the buyer.
              Upload at least one delivery photograph as proof of handover.
              The buyer will then be asked to acknowledge receipt.
            </div>

            <div className="twf-row">
              <label className="twf-label">Delivery photographs</label>
              <input
                className="twf-input"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setDeliveryPhotoFiles(Array.from(e.target.files || []))}
                disabled={deliveryUploadBusy || markDeliveredBusy}
              />
              {deliveryPhotos.length > 0 ? (
                <div className="twf-preview-grid">
                  {deliveryPhotos.map((photo, i) => (
                    <a
                      key={`delivery_photo_${photo.path || i}`}
                      className="twf-preview-card"
                      href={photo.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img src={photo.url} alt={`delivery evidence ${i + 1}`} />
                    </a>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="twf-row">
              <label className="twf-label">Handover notes</label>
              <textarea
                className="twf-input"
                rows={3}
                value={deliveryNote}
                onChange={(e) => setDeliveryNote(e.target.value)}
                placeholder="Optional notes about the handover"
                disabled={markDeliveredBusy}
              />
            </div>

            {deliveryUploadError ? (
              <div className="twf-error" style={{ marginBottom: 12 }}>
                {deliveryUploadError}
              </div>
            ) : null}

            <button
              className="twf-btn twf-btn--primary"
              type="button"
              onClick={markDelivered}
              disabled={markDeliveredBusy || deliveryUploadBusy || (deliveryPhotoFiles.length === 0 && deliveryPhotos.length === 0)}
            >
              {markDeliveredBusy || deliveryUploadBusy ? "Recording handover…" : "Mark as Delivered / Handed Over"}
            </button>
          </PanelSection>
        ) : null}

     {activeActionKey === "buyer_review_window" ? (
        <PanelSection title="Confirm Handover" active innerRef={buyerReviewRef}>
          <div className="twf-hint" style={{ marginBottom: 12 }}>
            Please confirm once you have received the item. If something is wrong,
            report the issue before confirming receipt.
          </div>

          <div className="twf-progress-meta">
            <span className="twf-pill">Handover review</span>
            <span className="twf-pill">Stage {idx + 1} of {ORDER.length}</span>
            <span className="twf-pill">{progressPct}% complete</span>
          </div>
          <div className="twf-progress">
            <div className="twf-progress-bar" style={{ width: `${progressPct}%` }} />
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
            <button
              className="twf-btn twf-btn--primary"
              type="button"
              onClick={buyerConfirmDelivery}
              disabled={buyerConfirmBusy || buyerIssueBusy}
            >
              {buyerConfirmBusy ? "Confirming…" : "I received the item"}
            </button>
          </div>

          <div className="twf-row">
            <label className="twf-label" htmlFor="buyer-issue-reason">
              Issue reason
            </label>
            <select
              id="buyer-issue-reason"
              className="twf-input"
              value={buyerIssueReason}
              onChange={(e) => setBuyerIssueReason(e.target.value)}
              disabled={buyerIssueBusy || buyerConfirmBusy}
            >
              <option value="">Select an issue reason</option>
              <option value="not_received">Item not received</option>
              <option value="item_not_as_described">Item not as described</option>
              <option value="damaged">Damaged item</option>
              <option value="missing_parts">Missing parts or accessories</option>
              <option value="suspected_counterfeit">Suspected counterfeit</option>
            </select>
          </div>

          <div className="twf-row">
            <label className="twf-label">Issue description</label>
            <textarea
              className="twf-input"
              rows={4}
              value={buyerIssueNote}
              onChange={(e) => setBuyerIssueNote(e.target.value)}
              placeholder="Describe the issue in detail so support can review it quickly."
            />
          </div>

          <button
            className="twf-btn twf-btn--danger"
            type="button"
            onClick={buyerReportIssue}
            disabled={buyerIssueBusy || buyerConfirmBusy}
          >
            {buyerIssueBusy ? "Reporting…" : "Report Issue"}
          </button>
        </PanelSection>
      ) : null}

        {activeActionKey === "complete_trade" ? (
          <PanelSection title="Complete Trade" active innerRef={adminCompleteRef}>
            <button
              className="twf-btn twf-btn--primary"
              type="button"
              onClick={completeTrade}
              disabled={completeBusy}
            >
              {completeBusy ? "Completing…" : "Complete Trade"}
            </button>
          </PanelSection>
        ) : null}

        {!activeActionKey ? (
          <StatusBlock
            title={viewerActionContext.isWaiting ? "Waiting status" : "Current Status"}
            hint={viewerActionContext.message}
          >
            <div>
              Current workflow stage:{" "}
              <span className="mono">{STAGE_LABELS[stage] || prettyKey(stage)}</span>
            </div>

            <div style={{ marginTop: 6 }}>
              Stage owner:{" "}
              <span className="mono">{currentStageOwner}</span>
            </div>
          </StatusBlock>
        ) : null}
      </div>
    </div>
  </div>
);
}